import * as React from 'react'
import {Alert, Col, DatePicker, Form, Icon, Modal, notification, Row, Select, Spin, Switch} from 'antd';
import {FormComponentProps} from 'antd/lib/form';
import {withTranslation, WithTranslation} from 'react-i18next';
import {connect} from 'redux-scaffolding-ts'
import {nameof} from 'src/core/utils/object';
import autobind from 'autobind-decorator';
import {GetFieldDecoratorOptions} from 'antd/lib/form/Form';
import HttpService, {formatMessage} from '../../../core/services/http.service';
import EditableTooltip from '../../../components/editable-tooltip/tooltip';
import moment from 'moment-timezone';
import {Moment} from 'moment';
import {TimesheetsGeneratorItem} from '../../../stores/timesheets-store';
import {resolve} from '../../../inversify.config';
import {EmployeesStore, EmployeeSummary} from '../../../stores/employees-store';
import {Query} from '../../../core/stores/dataStore';

let FormItem = Form.Item;
let { RangePicker } = DatePicker;

interface NewTimesheetsGeneratorViewProps {
    companyId: string,
    onClose: (id: string | undefined, item?: TimesheetsGeneratorItem) => void;
    onGenerated?: (fromDate: number, toDate: number) => void
}

interface NewTimesheetsGeneratorViewState {
    isBusy: boolean;
    result: string;
    isSuccess: boolean;
    employees: EmployeeSummary[];
}

interface ClassFormBodyProps extends WithTranslation {
    employees: EmployeeSummary[],
    onSave?: () => Promise<any>;
    getFieldDecorator<T extends Object = {}>(id: keyof T, options?: GetFieldDecoratorOptions): (node: React.ReactNode) => React.ReactNode;
    setFieldsValue: any;
}

interface ClassFormBodyState {
    startDate?: Moment;
    endDate?: Moment | null;
    isSelectingDate?: boolean;
    selectAll: boolean
}

class TimesheetsGeneratorFormBody extends React.Component<ClassFormBodyProps, ClassFormBodyState> {
    constructor(props: ClassFormBodyProps) {
        super(props);
        this.state = {
            selectAll: true
        }
    }

    @autobind
    private onEmployeesChange(e: string[]) {
        if (e.length != this.props.employees.length) {
            this.setState({selectAll:false})
        }
    }

    @autobind
    private onOpenCalendar(status: boolean) {
        if (status)
            this.setState({
                isSelectingDate: status
            })
        else
            this.setState({
                isSelectingDate: undefined
            })
    }

    @autobind
    private onSelectStartDate(date: Moment | null) {
        let selectedDate = date ? date.utc().startOf("day") : undefined
        this.setState({
            startDate: selectedDate,
        })
    }

    @autobind
    private onSelectEndDate(date: Moment | null) {
        let selectedDate = date ? date.startOf("day").utc() : undefined
        this.setState({
            endDate: selectedDate,
        })
    }

    @autobind
    private disabledStartDate(date: Moment | null) {
        if (!date)
            return false
        let dateUtc = date.utc().startOf("day")
        let day = dateUtc.day()
        let endDateCondition = this.state.endDate ? dateUtc >= this.state.endDate || Math.abs(dateUtc.diff(this.state.endDate, "weeks")) > 8 : false
        return endDateCondition || !(day === 1) || dateUtc > moment.utc().startOf("day")
    }

    @autobind
    private disabledEndDate(date: Moment | null) {
        if (!date)
            return false
        let dateUtc = date.utc().startOf("day")
        let day = dateUtc.day()
        let startDateCondition = this.state.startDate ? dateUtc <= this.state.startDate || Math.abs(dateUtc.diff(this.state.startDate, "weeks")) > 8 : false
        return startDateCondition || !(day === 0) || dateUtc > moment.utc().startOf("day").add(6, "days")
    }

    @autobind
    private async validateEndDate(rule: any, value: any) {
        if (value != undefined) {
            let endDate = (value as Moment).utc().startOf("day").unix()
            let startDate = this.state.startDate?.unix() ?? 0
            if (startDate > endDate) {
                throw new Error("End date must be lower than start date")
            }
        }
    }

    render() {
        const { t } = this.props;
        const { Option } = Select;

        var item = {} as TimesheetsGeneratorItem;
        return <Form id="modaForm" onKeyUp={(e) => { if (e.keyCode === 13 && this.props.onSave) { this.props.onSave(); } }} onSubmit={() => { if (this.props.onSave) { this.props.onSave(); } }}>
            <Row>
                <div>
                    <Icon type={"exclamation-circle"} style={{ marginRight: 5 }} />
                    {t('Timesheets may be generated from a range consisting of up to 8 weeks.')}
                </div>
            </Row>
            <Row gutter={24}>
                <Col span={12}>

                    <FormItem label={(
                        <EditableTooltip
                            name='new-project-dates'
                            visible={this.state.isSelectingDate === undefined ? undefined : !this.state.isSelectingDate}
                        >
                            {t("Start date")}</EditableTooltip>)}>
                        {this.props.getFieldDecorator(nameof<TimesheetsGeneratorItem>('fromDate'), {
                            rules: [{ required: true, message: t('Start date required') }],
                        })(
                            <DatePicker
                                style={{ width: "75%" }}
                                onOpenChange={this.onOpenCalendar}
                                onChange={this.onSelectStartDate}
                                disabledDate={this.disabledStartDate}
                                defaultPickerValue={this.state.endDate} />
                        )}
                    </FormItem>
                </Col>
                <Col span={12}>

                    <FormItem label={(
                        <EditableTooltip
                            name='new-project-dates'
                            visible={this.state.isSelectingDate === undefined ? undefined : !this.state.isSelectingDate}
                        >
                            { t("End date")}</EditableTooltip>)}>
                        {this.props.getFieldDecorator(nameof<TimesheetsGeneratorItem>('toDate'), {
                            rules: [{ required: true, message: t('End date required') }, { validator: this.validateEndDate }],
                        })(
                            <DatePicker
                                style={{ width: "75%" }}
                                onOpenChange={this.onOpenCalendar}
                                onChange={this.onSelectEndDate}
                                disabledDate={this.disabledEndDate}
                                defaultPickerValue={this.state.startDate} />
                        )}
                    </FormItem>

                </Col>
            </Row>
            <Row>
                <Col span={10}>
                    <FormItem label={t("Employees") } style={{ minWidth: 273, maxWidth: 273 }}>
                        {this.props.getFieldDecorator(nameof<TimesheetsGeneratorItem>('employees'), {
                    initialValue: this.props.employees.map((e: EmployeeSummary) => e.id),
                    rules: [{ required: true, message: t('At least one employee must be selected') },
                    ]
                })(
                    <Select
                        allowClear
                        mode="multiple"
                        className={"custom-select-multiple"}
                        onChange={this.onEmployeesChange}
                        style={{ minWidth: 300, maxWidth: 300, whiteSpace: "nowrap", maxHeight: '200px', overflow: 'auto' }}
                        placeholder={t('Employees')}
                        filterOption={(input, option) => {
                            if (option.props.children)
                                return option.props.children.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0;
                            return false;
                        }}>
                        {this.props.employees.map((e: EmployeeSummary) => <Option key={e.id} value={e.id}>{e.title}</Option>)}

                    </Select>
                )}
                    </FormItem>
                </Col >
                <Col span={2 }>
                    <Switch style={{ marginTop:"45px" }} checked={this.state.selectAll} onChange={() => { this.state.selectAll ? this.setState({ selectAll: false }, () => this.props.setFieldsValue({ employees: [] })) : this.setState({ selectAll: true }, () => this.props.setFieldsValue({ employees: this.props.employees.map((e: EmployeeSummary) => e.id) })) }} />
                </Col>
                <Col style={{ marginTop: "45px" }} span={6}>
                    <div>
                        {t('Select all employees')}
                    </div>
                </Col>
                        </Row>
        </Form>
    }
}

@connect(['employees', EmployeesStore])
class NewTimesheetsGeneratorView extends React.Component<NewTimesheetsGeneratorViewProps & WithTranslation & FormComponentProps, NewTimesheetsGeneratorViewState> {
    @resolve(HttpService)
    private httpService!: HttpService

    constructor(props: NewTimesheetsGeneratorViewProps & WithTranslation & FormComponentProps) {
        super(props);
        this.state = {
            isBusy: false,
            result: "",
            isSuccess: true,
            employees:[]
        }
    }

    componentWillMount() {
        var query = {
                searchQuery: '',
                orderBy: [{ field: 'name', direction: 'Ascending', useProfile: false }],
                skip: 0,
                take: 1000,
        }
        this.loadEmployees(query as Query)
    }

    private get employeesStore() {
        return (this.props as any).employees as EmployeesStore;
    }

    @autobind
    private async loadEmployees(query: Query) {
        this.setState({ isBusy: true });
        this.employeesStore.changeUrl(this.props.companyId)
        await this.employeesStore.getAllAsync(query).then(result => {
            let employees = result.items as EmployeeSummary[]
            this.setState({
                isBusy: false,
                employees: employees,
            });
        }).catch(error => {
            this.setState({ result: formatMessage(error), isBusy: false })
        });
    }

    @autobind
    private countDownNotification(_notification: any, message: string, description: string, key: string) {
        let secondsToGo = 4;
        _notification({
            key: key,
            message: message + `(${secondsToGo}).`,
            description: description
        });
        const timer = setInterval(() => {
            secondsToGo -= 1;
            _notification({
                key: key,
                message: message + `(${secondsToGo}).`,
                description: description,
                onClose: () => clearInterval(timer),
            });
        }, 1000);
        setTimeout(() => {
            clearInterval(timer);
            notification.close(key);
        }, secondsToGo * 1000);
    }
    
    @autobind
    private async onCreateNewItem() {
        var self = this;
        return new Promise((resolve, reject) => {
            self.props.form.validateFields(event => {
                var values = self.props.form.getFieldsValue();
                var { fromDate, toDate, employees } = values
                if (!event) {
                    if (fromDate && toDate)
                        this.generateTimesheets(fromDate.unix(), toDate.unix(), employees)
                        
                }
            });
        })
    }

    @autobind
    private generateTimesheets(fromDate: number, toDate: number, employees: string[]) {
        var self = this
        const { t } = this.props;
        this.setState({
            isBusy: true
        })
        var parameters: string = `?startDateStamp=${fromDate}&endDateStamp=${toDate}`
        this.httpService.post<any, string[]>(`api/v1/admin/companies/${this.props.companyId}/timesheets/generate${parameters}`, employees).then(result => {
            self.setState({
                isBusy: false,
                isSuccess: true
            })
            self.onGenerated(fromDate, toDate)
            this.countDownNotification(notification.success, t("Notification"), t("Generation has begun. The timesheets will be available later."), "success");
        }).catch(error => {
            self.setState({
                isBusy: false,
                isSuccess: false,
                result: formatMessage(error)
            })
            this.countDownNotification(notification.error, t("Error"), t("There was an error"), "failure");
        })
    }

    @autobind
    private onCancelNewItem() {
        this.props.onClose(undefined);
    }

    @autobind
    private onGenerated(fromDate: number, toDate: number) {
        const { t } = this.props;
        if (this.props.onGenerated) {
            this.props.onGenerated(fromDate, toDate)
        }
    }

    public render() {
        const { getFieldDecorator, setFieldsValue } = this.props.form;
        const { t } = this.props;
        return (
            <Modal
                okText={t("Generate")}
                cancelText={t("Cancel")}
                maskClosable={false}
                visible
                onCancel={this.onCancelNewItem}
                onOk={this.onCreateNewItem}
                closable={false}
                width='800px'
                title={t("Generate timesheets")}>
                {this.state.result && !this.state.isSuccess &&
                    <Alert type='error'
                        message={t("Error")}
                        description={t(formatMessage(this.state.result))}
                    />
                }
                <Spin spinning={this.state.isBusy}>
                    <TimesheetsGeneratorFormBody i18n={this.props.i18n} t={this.props.t} tReady={this.props.tReady} getFieldDecorator={getFieldDecorator} onSave={this.onCreateNewItem} employees={this.state.employees} setFieldsValue={setFieldsValue} />
                </Spin>
            </Modal>
        );
    }
}

// Wire up the React component to the Redux store
export default withTranslation()(Form.create({})(NewTimesheetsGeneratorView as any) as any) as any as React.ComponentClass<NewTimesheetsGeneratorViewProps>;